import React, { useEffect, useState } from "react";

import { QueryClientProvider } from "@tanstack/react-query";
import { createPortal } from "react-dom";

import {
  Alert,
  Button,
  Caption,
  Divider,
  Drawer,
  Flex,
} from "@olist/design-system";
import {
  AttachOutline,
  CloseOutline,
  DocumentOutline,
  ProfileOutline,
} from "@olist/design-system-icons";

import { ChatHistory } from "./chat-history";
import { EnhancedChat } from "./enhanced-chat";
import {
  ChatContainer,
  GradientText,
  GradientTextAnimated,
  HistoryContainer,
  SButtonIcon,
  SInput,
  TransferDivider,
} from "./styles";
import { WelcomeStep } from "./welcome-step";
import { useSendMessage } from "../../hooks";
import { useSalesforceChat } from "../../hooks/useSalesforceEnhancedChat";
import { AiIcon, GradientIcon } from "icons";
import { queryClient } from "utils/react-query";

interface MessageProps {
  id: string;
  content: string;
  sender: "user" | "agent";
  timestamp: Date;
}

function Chat({
  accessToken,
  isOpen,
  isPortal,
  onClose,
  theme,
}: {
  isOpen: boolean;
  isPortal?: boolean;
  onClose: () => void;
  theme: "light" | "dark";
  accessToken: string;
}) {
  const [messages, setMessages] = useState<MessageProps[]>([]);
  const [chatId, setChatId] = useState<string | undefined>();
  const [inputValue, setInputValue] = useState("");
  // const [isAlreadyRender, setIsAlreadyRender] = useState(false);
  const [isTransfer, setIsTransfer] = useState(false);

  const {
    isError,
    isPending: isLoading,
    mutate: sendMessage,
  } = useSendMessage();

  const {
    connected,
    error,
    loading,
    messages: enhancedMessages,
    sendMessage: sendEnhancedChatMessage,
    session,
    startSession,
    typing,
  } = useSalesforceChat({
    orgId: process.env.REACT_SF_ORG_ID || "00DHr000002jN5F",
    esDeveloperName:
      process.env.REACT_SF_ES_DEVELOPER_NAME ||
      "Atendimento_ERP_ajuda_teste_lis",
    scrtUrl:
      process.env.REACT_SF_SCRT_URL || "https://olist.my.salesforce-scrt.com",
  });

  const scrollToLastMessage = () => {
    const messagesdiv = document.querySelectorAll("div#messages");
    const container = document.querySelector("div#container-scroll");
    const lastMessage = messagesdiv.item(messages.length - 2);
    if (
      lastMessage instanceof HTMLElement &&
      container instanceof HTMLElement
    ) {
      container.scrollTop = lastMessage.scrollHeight;
    }
  };

  const handleSendMessage = async (value?: string) => {
    if (!inputValue.trim()) return;

    if (isTransfer && connected && session) {
      await sendEnhancedChatMessage({ session, text: value || inputValue }, {
        onError: (errorAxios) => {
          console.log("estou com erro, enhancedChat", errorAxios);
        },
      });
      setInputValue("");
      return;
    }

    const userMessage: MessageProps = {
      id: Date.now().toString(),
      content: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    sendMessage(
      { accessToken, prompt: inputValue, chatId },
      {
        onSuccess: (data) => {
          setChatId(data?.chat_id);
          const agentMessage: MessageProps = {
            id: data?.chat_message_id || "",
            content: data?.answer,
            sender: "agent",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, agentMessage]);
          scrollToLastMessage();
        },
        onError: (errorAxios) => {
          console.log("estou com erro, agent", errorAxios);
        },
      }
    );
  };

  const handleTransferChat = () => {
    setIsTransfer(true);
    startSession();
  };

  useEffect(() => {
    const container = document.querySelector("div#container-scroll");
    if (container instanceof HTMLElement) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, enhancedMessages]);

  useEffect(() => {
    const container = document.querySelector("div#container-scroll");
    if (isOpen && container instanceof HTMLElement) {
      container.scrollTop = container.scrollHeight;
    }
  }, [isOpen]);

  if (isPortal) {
    const container = document.getElementById("lis");
    const body = document.querySelector("body");
    const app = document.getElementById("app");
    if (container !== null && body !== null && app !== null) {
      body.style.display = "flex";
      body.style.flexDirection = "row-reverse";
      app.style.width = "-webkit-fill-available";

      return createPortal(
        <aside
          style={{
            width: "373px",
            height: "-webkit-fill-available",
            maxHeight: "100vh",
          }}
        >
          <ChatContainer DSTheme={theme} isPortal={isPortal}>
            <Flex flexDirection="column" gap="stack.medium">
              <Flex
                width="100%"
                justifyContent="space-between"
                gap="inline.small"
                alignItems="center"
              >
                {messages.length > 0 || enhancedMessages.length > 0 ? (
                  <Flex gap="inline.2xsmall" alignItems="center">
                    {isTransfer && (
                      <Flex
                        backgroundColor="neutral.softer"
                        justifyContent="center"
                        alignItems="center"
                        borderRadius="circle"
                        padding="inset.2xsmall"
                      >
                        <GradientIcon />
                      </Flex>
                    )}
                    <GradientText>
                      {isTransfer ? "Atendente" : "Assistente virtual"}
                    </GradientText>
                    <Caption>da Olist</Caption>
                  </Flex>
                ) : (
                  <div />
                )}
                <SButtonIcon
                  DSTheme={theme}
                  icon={<CloseOutline color="rgb(105, 109, 140)" />}
                  onClick={onClose}
                />
              </Flex>
              {(isError || !!error) && (
                <Flex height="100px">
                  <Alert
                    showIcon
                    variation="error"
                    description="Ops tivemos um problema, favor tente novamente mais tarde"
                  />
                </Flex>
              )}
            </Flex>

            {messages.length === 0 && <WelcomeStep />}
            <HistoryContainer id="container-scroll">
              <ChatHistory
                messages={messages}
                theme={theme}
              />
              {isTransfer && (
                <>
                  <TransferDivider>
                    Atendente da Olist entrou no chat
                  </TransferDivider>
                  <EnhancedChat
                    messages={enhancedMessages}
                    loading={loading}
                    theme={theme}
                    error={error}
                    sendMessage={handleSendMessage}
                    typing={typing}
                  />
                </>
              )}
            </HistoryContainer>
            {isLoading && (
              <Flex gap="inline.small">
                <AiIcon />
                <GradientTextAnimated DSTheme={theme}>
                  Buscando a melhor solução...
                </GradientTextAnimated>
              </Flex>
            )}
            {isLoading === false &&
              messages.length > 0 &&
              isTransfer === false && (
                <Flex flexDirection="column" gap="stack.small">
                  <Flex justifyContent="space-between">
                    <Button iconLeft={DocumentOutline} variation="secondary">
                      abrir chamado
                    </Button>
                    <Button onClick={onClose}>fechar conversa</Button>
                  </Flex>
                  <Button
                    iconLeft={ProfileOutline}
                    variation="secondary"
                    onClick={handleTransferChat}
                  >
                    falar com atendente
                  </Button>
                </Flex>
              )}
            <Flex flexDirection="column" gap="stack.small">
              <SInput
                name="chat-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={
                  isTransfer && !connected
                    ? "Reconectando..."
                    : "Digite sua mensagem..."
                }
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                DSTheme={theme}
                isLoading={isLoading || (isTransfer && loading)}
                disabled={isTransfer && (!connected || !session)}
              />
              {isTransfer && (
                <Button
                  variation="secondary"
                  iconLeft={AttachOutline}
                  onClick={() => console.log("oi")}
                  disabled={!connected || !session}
                >
                  anexar documento
                </Button>
              )}
            </Flex>
          </ChatContainer>
        </aside>,
        container
      );
    }
  }

  return (
    <Drawer
      isOpen={isOpen}
      closeDrawer={() => {
        // setIsAlreadyRender(true);
        onClose();
      }}
      width="373px"
    >
      <ChatContainer DSTheme={theme} isPortal={isPortal}>
        <Flex flexDirection="column" gap="stack.medium">
          <Flex
            width="100%"
            justifyContent="space-between"
            gap="inline.small"
            alignItems="center"
          >
            {messages.length > 0 || enhancedMessages.length > 0 ? (
              <Flex gap="inline.1xsmall" alignItems="center">
                <GradientText>Assistente virtual</GradientText>
                <Caption>da Olist</Caption>
              </Flex>
            ) : (
              <div />
            )}
            <SButtonIcon
              DSTheme={theme}
              icon={<CloseOutline />}
              onClick={onClose}
            />
          </Flex>
          {(isError || !!error) && (
            <Flex height="100px">
              <Alert
                showIcon
                variation="error"
                description="Ops tivemos um problema, favor tente novamente mais tarde"
              />
            </Flex>
          )}
        </Flex>

        {messages.length === 0 && <WelcomeStep />}
        <ChatHistory
          messages={messages}
          // isLoading={isLoading}
          theme={theme}
        // isAlreadyRender={isAlreadyRender}
        />
        {isTransfer && (
          <>
            <Divider />
            <EnhancedChat
              messages={enhancedMessages}
              loading={loading}
              theme={theme}
              error={error}
              sendMessage={handleSendMessage}
              typing={typing}
            />
            {/* <SInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={
                    connected ? "Digite sua mensagem..." : "Reconectando..."
                  }
                  disabled={!connected || !session}
                  onKeyDown={(e) =>
                    e.key === "Enter" && connected && handleSendEnhanced()
                  }
                  DSTheme={theme}
                /> */}
          </>
        )}
        {isLoading && (
          <Flex gap="inline.small">
            <AiIcon />
            <GradientTextAnimated DSTheme={theme}>
              Buscando a melhor solução...
            </GradientTextAnimated>
          </Flex>
        )}
        <SInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Digite sua mensagem..."
          onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          DSTheme={theme}
        />
      </ChatContainer>
    </Drawer>
  );
}

export function LisChat(props: {
  isOpen: boolean;
  isPortal?: boolean;
  onClose: () => void;
  theme: "light" | "dark";
  accessToken: string;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <Chat {...props} />
    </QueryClientProvider>
  );
}
